import type { JSX } from 'react';
import type { InviteChannel } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';
import { channelLabel } from '@/lib/outreach-copy';
import { cn } from '@/lib/utils';
import { COLUMN_ROLES, type ColumnMapping, type ColumnRole } from '@/lib/bulk-import/columns';
import type { ImportRow } from '@/lib/bulk-import/rows';
import { HandleCell, StepPanel, plural } from './shared';

interface ColumnsStepProps {
  columns: string[];
  mapping: ColumnMapping;
  onMapping: (role: ColumnRole, col: number | null) => void;
  hasHeader: boolean;
  onHasHeader: (v: boolean) => void;
  fallbackChannel: InviteChannel | null;
  onFallbackChannel: (c: InviteChannel | null) => void;
  allowedChannels: InviteChannel[];
  hasPlainHandles: boolean;
  /** Rows as read with the current choices. */
  rows: ImportRow[];
  /** A few raw lines for the preview. */
  sample: string[][];
  onBack: () => void;
  onNext: () => void;
}

const ROLE_HINTS: Record<ColumnRole, string> = {
  handle: 'Profile links or @handles. The platform comes from each link.',
  niche: "Matched to your team's niches in the next step.",
  email: 'Blank, NIL, N/A and similar are left empty.',
  date: "Goes into the invite's notes. It never changes when the invite was logged.",
  notes: 'Added to each invite’s notes.',
};

export function ColumnsStep(props: ColumnsStepProps): JSX.Element {
  const { columns, mapping, rows } = props;
  const valid = rows.filter((r) => !r.error && r.duplicateOf === null).length;
  const invalid = rows.filter((r) => r.error).length;
  const dupes = rows.filter((r) => r.duplicateOf !== null).length;
  const byChannel = new Map<string, number>();
  for (const r of rows) if (!r.error && r.duplicateOf === null && r.channel) byChannel.set(r.channel, (byChannel.get(r.channel) ?? 0) + 1);
  const mappedCols = new Set(Object.values(mapping).filter((v): v is number => v !== null));

  return (
    <StepPanel
      title="Check the columns"
      description="We guessed which column holds what. Change any that are wrong. Only the handle column is required."
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {COLUMN_ROLES.map(({ role, label, required }) => (
          <div key={role} className="grid content-start gap-1.5">
            <Label htmlFor={`col-${role}`}>
              {label}
              {required && <span className="ml-1 text-destructive">*</span>}
            </Label>
            <Combobox
              id={`col-${role}`}
              value={mapping[role] === null ? '' : String(mapping[role])}
              onChange={(v) => props.onMapping(role, v === '' ? null : Number(v))}
              options={columns.map((name, i) => ({ value: String(i), label: name }))}
              allowAll={!required}
              allLabel="None"
              placeholder={required ? 'Pick a column' : 'None'}
            />
            <p className="text-xs text-muted-foreground">{ROLE_HINTS[role]}</p>
          </div>
        ))}

        <div className="grid content-start gap-1.5">
          <Label htmlFor="fallback-channel">Platform for plain handles</Label>
          <Combobox
            id="fallback-channel"
            value={props.fallbackChannel ?? ''}
            onChange={(v) => props.onFallbackChannel(v === '' ? null : (v as InviteChannel))}
            options={props.allowedChannels.map((c) => ({ value: c, label: channelLabel(c) }))}
            allowAll
            allLabel="None (skip plain handles)"
          />
          <p className={cn('text-xs', props.hasPlainHandles && !props.fallbackChannel ? 'font-medium text-warning' : 'text-muted-foreground')}>
            {props.hasPlainHandles
              ? 'Some rows are a bare @handle with no link. Which platform are they on?'
              : 'Used for rows with a bare @handle instead of a link.'}
          </p>
        </div>
      </div>

      <label className="flex w-fit cursor-pointer items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4 accent-[var(--brand)]"
          checked={props.hasHeader}
          onChange={(e) => props.onHasHeader(e.target.checked)}
        />
        First row is column names
      </label>

      {/* Preview of the first rows, mapped columns highlighted. */}
      <div className="table-frame overflow-x-auto">
        <table className="w-full whitespace-nowrap text-sm" aria-label="File preview">
          <thead className="text-left">
            <tr>
              {columns.map((name, i) => (
                <th
                  key={i}
                  className={cn(
                    'px-3 py-2 text-xs font-semibold',
                    mappedCols.has(i) ? 'text-brand' : 'text-muted-foreground/80',
                  )}
                >
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {props.sample.map((r, ri) => (
              <tr key={ri} className="border-t border-border">
                {columns.map((_, ci) => (
                  <td
                    key={ci}
                    className={cn('max-w-[28ch] truncate px-3 py-2', !mappedCols.has(ci) && 'text-muted-foreground/70')}
                    title={r[ci] ?? ''}
                  >
                    {r[ci] || '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-3 rounded-lg border border-border px-4 py-3 text-sm sm:grid-cols-[1fr_auto] sm:items-center">
        <div className="space-y-1">
          <p>
            <span className="font-semibold">{plural(valid, 'creator')}</span> found
            {byChannel.size > 0 && (
              <span className="text-muted-foreground">
                {' '}
                ({[...byChannel.entries()].map(([c, n]) => `${n.toLocaleString('en-US')} ${channelLabel(c)}`).join(', ')})
              </span>
            )}
            .
          </p>
          <p className="text-xs text-muted-foreground">
            {plural(invalid, 'row')} can&apos;t be read and {plural(dupes, 'row')} repeat an earlier one. You&apos;ll see
            each of them, with the reason, in Review.
          </p>
          {rows[0] && (
            <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              First row reads as <HandleCell row={rows[0]} />
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2 sm:justify-end">
          <Button variant="outline" onClick={props.onBack}>
            Back
          </Button>
          <Button onClick={props.onNext} disabled={mapping.handle === null || valid === 0}>
            Check {plural(valid, 'creator')}
          </Button>
        </div>
      </div>
    </StepPanel>
  );
}
